import { useTranslation } from "react-i18next";
import dairectArrowUpRight from "../../../assets/img/icons/dairect-arrow-up-right.svg";
import cardMissionIcon from "../../../assets/img/rebranding/home-rebrand-card-mission.svg";
import cardTeamIcon from "../../../assets/img/rebranding/home-rebrand-card-team.svg";
import cardVisionIcon from "../../../assets/img/rebranding/home-rebrand-card-vision.svg";
import { fireTagManagerEvent } from "../../../assets/Services/tagmanager";
import HomeRebranding from "./HomeRebranding";

const DAIRECT_URL = "https://dairect.io";

const DARK_DOTS_STYLE = {
  backgroundColor: "#091112",
  backgroundImage:
    "radial-gradient(circle, rgba(154, 254, 227, 0.2) 1px, transparent 1px)",
  backgroundSize: "30px 30px",
} as const;

const DARK_VIGNETTE_STYLE = {
  backgroundImage:
    "radial-gradient(75.6% 41% at 50% 50%, rgba(9, 17, 18, 0) 65.319%, #091112 100%)",
} as const;

const STORY_OVERLAY_STYLE = {
  backgroundImage:
    "linear-gradient(90deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.05) 100%), radial-gradient(75.6% 32.3% at 50% 50%, rgba(9, 17, 18, 1) 50%, rgba(9, 17, 18, 0) 100%)",
} as const;

const DISCOVER_DOTS_STYLE = {
  backgroundImage:
    "radial-gradient(circle, rgba(255, 255, 255, 0.85) 1px, transparent 1px)",
  backgroundSize: "20px 20px",
  opacity: 0.5,
} as const;

const DISCOVER_VIGNETTE_STYLE = {
  backgroundImage:
    "radial-gradient(80% 140% at 50% -20%, rgba(255, 255, 255, 0.95) 20%, rgba(255, 255, 255, 0.15) 70%, rgba(255, 255, 255, 0) 100%)",
} as const;

const REMAINS_CARDS = [
  {
    icon: cardVisionIcon,
    titleKey: "ds.home_rebrand_section3_card1_title",
    bodyKey: "ds.home_rebrand_section3_card1_body",
  },
  {
    icon: cardTeamIcon,
    titleKey: "ds.home_rebrand_section3_card2_title",
    bodyKey: "ds.home_rebrand_section3_card2_body",
  },
  {
    icon: cardMissionIcon,
    titleKey: "ds.home_rebrand_section3_card3_title",
    bodyKey: "ds.home_rebrand_section3_card3_body",
  },
] as const;

export default function HomeRebrandingHomepage() {
  const { t } = useTranslation();

  const openDairect = (campaign: string) => {
    fireTagManagerEvent("click_banner", {
      name: campaign,
      url: DAIRECT_URL,
    });

    window.open(
      `${DAIRECT_URL}/?utm_source=takyon&utm_medium=homepage&utm_campaign=${campaign}`,
      "_blank",
      "noopener,noreferrer",
    );
  };

  return (
    <div className="bg-[#091112]">
      <HomeRebranding />

      <section className="relative isolate overflow-hidden bg-[#091112] px-5 py-[100px] lg:px-12 lg:py-[240px]">
        <div className="pointer-events-none absolute inset-0" style={DARK_DOTS_STYLE} />
        <div
          className="pointer-events-none absolute inset-0 z-[1]"
          style={STORY_OVERLAY_STYLE}
        />

        <div className="relative z-[2] mx-auto w-full max-w-[1200px]">
          <p className="mx-auto w-full max-w-[350px] [font-family:'Geist',_sans-serif] text-left text-[24px] font-normal leading-[34px] tracking-[-0.72px] text-white lg:max-w-[968px] lg:text-center lg:tracking-[-0.48px]">
            {t("ds.home_rebrand_section2_paragraph1")}
          </p>
          <p className="mx-auto mt-[30px] w-full max-w-[350px] [font-family:'Geist',_sans-serif] text-left text-[24px] font-normal leading-[34px] tracking-[-0.72px] text-white lg:max-w-[968px] lg:text-center lg:tracking-[-0.48px]">
            {t("ds.home_rebrand_section2_paragraph2")}
          </p>
        </div>
      </section>

      <section className="relative isolate overflow-hidden bg-[#091112] px-5 py-[100px] lg:px-12 lg:py-[240px]">
        <div className="pointer-events-none absolute inset-0" style={DARK_DOTS_STYLE} />
        <div
          className="pointer-events-none absolute inset-0 z-[1]"
          style={DARK_VIGNETTE_STYLE}
        />

        <div className="relative z-[2] mx-auto w-full max-w-[1200px]">
          <h2 className="m-0 [font-family:'Geist',_sans-serif] text-left text-[24px] font-medium leading-[30px] tracking-[-0.72px] text-white lg:text-center lg:text-[36px] lg:leading-[44px] lg:tracking-[-1.08px]">
            {t("ds.home_rebrand_section3_title")}
          </h2>

          <div className="mt-10 grid grid-cols-1 gap-4 lg:grid-cols-3 lg:gap-5">
            {REMAINS_CARDS.map((card) => (
              <article
                key={card.titleKey}
                className="rounded-[24px] border border-white/5 bg-white/[0.03] p-5 backdrop-blur-[25px] lg:min-h-[244px]"
              >
                <div
                  className="inline-flex h-[50px] w-[50px] items-center justify-center rounded-xl border border-white/10 bg-white/10"
                  aria-hidden="true"
                >
                  <img src={card.icon} alt="" className="h-6 w-6" />
                </div>

                <h3 className="mt-3 [font-family:'Geist',_sans-serif] text-[20px] font-medium leading-6 tracking-[-0.6px] text-white">
                  {t(card.titleKey)}
                </h3>
                <p className="mt-2 [font-family:'Geist',_sans-serif] text-[16px] font-normal leading-[22px] tracking-[-0.32px] text-white/60">
                  {t(card.bodyKey)}
                </p>
              </article>
            ))}
          </div>

          <p
            className="mt-10 hidden [font-family:'Geist',_sans-serif] text-center text-[18px] font-normal leading-6 tracking-[-0.36px] text-white/60 lg:block [&_strong]:font-medium [&_strong]:text-white"
            dangerouslySetInnerHTML={{
              __html: t("ds.home_rebrand_section3_footnote_html"),
            }}
          />
        </div>
      </section>

      <section className="relative isolate overflow-hidden border-b-2 border-white/20 bg-[linear-gradient(180deg,#f3f3f3_0%,#9afee3_100%)] px-5 py-[100px] lg:px-12 lg:py-[240px]">
        <div
          className="pointer-events-none absolute inset-0"
          style={DISCOVER_DOTS_STYLE}
        />
        <div
          className="pointer-events-none absolute inset-0 z-[1]"
          style={DISCOVER_VIGNETTE_STYLE}
        />

        <div className="relative z-[2] mx-auto flex w-full max-w-[1200px] flex-col items-center text-center">
          <h2 className="m-0 [font-family:'Geist',_sans-serif] text-[30px] font-medium leading-[32px] tracking-[-0.9px] text-[#091112] lg:text-[36px] lg:leading-[44px] lg:tracking-[-1.08px]">
            {t("ds.home_rebrand_section4_title")}
          </h2>
          <p
            className="mt-4 max-w-[264px] [font-family:'Geist',_sans-serif] text-[16px] font-normal leading-[22px] tracking-[-0.32px] text-[#091112] [&_strong]:font-semibold lg:max-w-[306px] lg:text-[18px] lg:tracking-[-0.36px]"
            dangerouslySetInnerHTML={{
              __html: t("ds.home_rebrand_section4_subtitle_html"),
            }}
          />

          <button
            type="button"
            className="mt-[30px] inline-flex items-center justify-center gap-2 rounded-2xl border border-white/10 bg-[#091112] px-4 py-3 [font-family:'Geist',_sans-serif] text-[16px] font-medium leading-6 text-white transition-opacity hover:opacity-90"
            onClick={() => openDairect("go_to_dairect_bottom")}
          >
            <span>{t("ds.home_rebrand_section4_button")}</span>
            <img
              src={dairectArrowUpRight}
              alt=""
              aria-hidden="true"
              className="h-5 w-5 [filter:brightness(0)_invert(1)]"
            />
          </button>
        </div>
      </section>
    </div>
  );
}
